import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule, } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../../../lib/axios';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../../../AgGridStyle.css';
import HomeIcon from '@mui/icons-material/Home';
import PropertyFilter from "../../../PropertyFilter";

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule,]);

const GenerateCouponTable = () => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const UserId = sessionStorage?.getItem('UserId');
  const permissions = usePermissions("Coupon Master");
  const { AddRec = "0" } = permissions || {};
  const gridRef = useRef();
  const navigate = useNavigate();
  const [lastTap, setLastTap] = useState(0);
  const [tappedRowId, setTappedRowId] = useState(null);
  const DOUBLE_TAP_DELAY = 300;
  const isMobile = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const globalProperty = useSelector(selectGlobalProperty);
  const [SelectedProperty, setSelectedProperty] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});

  const colDefs = useMemo(() => [
    { field: 'RowNum', headerName: 'Sr.No', width: 70 },
    { field: 'PropName', headerName: 'Property', width: 180 },
    { field: 'Name', headerName: 'Authority', width: 150 },
    { field: 'ResidentName', headerName: 'Resident', width: 150 },
    { field: 'MobileNo', headerName: 'MobileNo', width: 100 },
    { field: 'BookNo', headerName: 'BookNo', width: 100 },
     { field: 'RoomBedName', headerName: 'BedNo', width: 80 },

    { field: 'CouponNo', headerName: 'CouponNo', width: 100 },
    { field: 'PromoCodeName', headerName: 'PromoCode', width: 110 },
    { field: 'DiscType', headerName: 'DiscType', width: 80 },
    { field: 'DiscAmt', headerName: 'Discount', width: 90 },
        { field: 'CreatedDate', headerName: 'Created Date', width: 120, filter: 'agDateColumnFilter' },
    { field: 'RedeemDt', headerName: 'RedeemDt', width: 100, filter: 'agDateColumnFilter' },
     { field: 'RedeemStatus', headerName: 'Redeem Status', width: 120,
      cellStyle: (params) => ({
        color: params.value === 'Yes'  ? 'green' : 'red',
        fontWeight: 'bold',
      })
       },
    { field: 'ApprovalBy', headerName: 'Approved By', width: 100 },
    { field: 'ApprovalDt', headerName: 'Approved Date', width: 100, filter: 'agDateColumnFilter' },

    {
      field: 'ApprovalFlag', headerName: 'Approval Status', width: 110,
      cellStyle: (params) => ({
        color: params.value === 'Y' || params.value === 'Approved' ? 'green' : 'red',
        fontWeight: 'bold',
      })
    },
    {
      field: 'Status', headerName: 'Status', width: 70,
      cellStyle: (params) => ({
        color: params.value === 'Active' || params.value === '1' ? 'green' : 'red',
        fontWeight: 'bold',
      })
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    width: 100,
    tooltipValueGetter: (params) => params.value ?? '',
  }), []);

  const handleCellClicked = useCallback((params) => {
    if (params.column.isHeader || !params.data) return;
    const couponId = params.data.CouponId;
    if (!couponId) return;

    const currentTime = Date.now();
    const timeSince = currentTime - lastTap;

    setTappedRowId(couponId);
    setTimeout(() => setTappedRowId(null), 300);

    if (isMobile()) {
      navigate('/generate-coupon-master', { state: { CouponId: couponId, mode: 'view' } });
      return;
    }

    if (timeSince > 0 && timeSince < DOUBLE_TAP_DELAY) {
      navigate('/generate-coupon-master', { state: { CouponId: couponId, mode: 'view' } });
    }
    setLastTap(currentTime);
  }, [navigate, lastTap]);

  useEffect(() => {
    fetchCouponAuthorityData();
  }, [SelectedProperty]);


  const mapApprovalFlag = (flag) => {
    switch (flag) {
      case "Y":
        return "Approved";
      case "N":
        return "Unapproved";
      default:
        return flag || "Unapproved";
    }
  };

  const fetchCouponAuthorityData = async () => {
    try {
      const response = await axiosInstance.post(
        "MstPromoCodeCoupon/GetMstPromoCodeCouponDashBoard",
        {
          start: 1,
          PageSize: 100,
          SearchText: "",
        }
      );

      const { data: { Status, Data, responseStatusCode, Message } } = response;

      if (Status === 0 && Array.isArray(Data) && responseStatusCode === 1) {
        const formatDate = (dateStr) => {
          if (!dateStr) return "";
          return new Date(dateStr).toLocaleDateString("en-IN"); // "YYYY-MM-DD" format
        };

        const mappedData = Data.map((item, index) => ({
          ...item,
          id: item.CouponId, // For DataGrid
          ApprovalFlag: mapApprovalFlag(item.ApprovalFlag),
          RowNum: index + 1,
          PropName: item.PropName || "",
          ResidentName: item.ResidentName || "",
          MobileNo: item.MobileNo || "",
          DiscType: item.DiscType || "",
          DiscAmt: item.DiscAmt || 0,
          RedeemDt: formatDate(item.RedeemDt),
          ApprovalBy: item.ApprovalBy || "",
          ApprovalDt: formatDate(item.ApprovalDt),
          CreatedDate:formatDate(item.CreatedDate),
          RedeemStatus:item.BookId !==1 ? 'Yes': 'No',
          Status: item.Status == null ? '' : (item.Status == 1 ? 'Active' : 'Inactive')
        }));

        setRowData(mappedData);
      } else {
        toast.info("No record found.", Message);
      }
    } catch (error) {
      toast.error("Error fetching coupon data:", error);
    }
  };

  const onGridReady = useCallback((params) => {
    // No sizeColumnsToFit here to preserve flex sizing
  }, []);

  const handleRowDoubleClick = useCallback((params) => {
    const compId = params?.data?.CouponId;
    const BookId=params?.data?.BookId
    if (compId) {
      navigate('/generate-coupon-master', { state: { CouponId: compId,BookId:BookId, mode: 'view' } });
    }
  }, [navigate]);

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      navigate('/generate-coupon-master');
    }
  };

  const handleClearFilters = () => {
    gridRef.current?.api?.setFilterModel(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '90vh',
        padding: '10px',
        backgroundColor: 'rgb(249, 249, 249)',
        position: 'relative'
      }}
    >
      <ToastContainer />
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: { xs: "8px 16px", sm: "12px 16px" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: "10px auto",
          maxWidth: "95%",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" }
          }}
        >
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Box component="span">Home</Box>
          </Link>
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={() => navigate('/masters/company')}
          >
            Company
          </Link>

          <Typography color="text.primary" sx={{ fontWeight: "500" }}>
            Coupon Master
          </Typography>
        </Breadcrumbs>

        {/* Buttons container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: { xs: 1, sm: 1, md: 0 },
            flexShrink: 0,
          }}
        >

          <PropertyFilter
            SelectedProperty={SelectedProperty}
            setSelectedProperty={setSelectedProperty}
            searchTerms={searchTerms}
            setSearchTerms={setSearchTerms}
            userId={UserId}
            label="Filter by Property"
            width={250}
          />
          <Tooltip title='Clear all applied filter' arrow>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              size="small"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                borderColor: "#635bff",
                color: "#635bff",
                "&:hover": {
                  borderColor: "#524ccc",
                  backgroundColor: "rgba(99, 91, 255, 0.04)",
                }
              }}
            >
              Clear Filters
            </Button>
          </Tooltip>

          <Tooltip
            title={AddRec !== "1" ? "You have no permission to add record" : "Add New Record"}
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handlePermittedClick}
                size="small"
                disabled={AddRec !== "1"}
                sx={{
                  background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                  color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                  borderRadius: "50px",
                  padding: "6px 15px",
                  boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                  "&:hover": {
                    background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                  }
                }}
              >
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> New
              </Button>
            </span>
          </Tooltip>

          <Tooltip title='Previous Page' arrow>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              justifyContent: "flex-start"
            }}
            >
              <IconButton
                onClick={() => navigate('/masters/company')}
                sx={{
                  backgroundColor: '#635bff',
                  borderRadius: '50%',
                  width: 35,
                  height: 35,
                  '&:hover': {
                    backgroundColor: '#635bff',
                  },
                  boxShadow: 3,
                }}
              >
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Box className="table-grid ag-theme-alpine"
        sx={{ height: 'calc(97% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
      >
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
          >
            <CircularProgress size="3rem" sx={{ color: '#635bff' }} />

            <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
              Loading data...
            </Typography>
          </Box>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={1000}
            onRowDoubleClicked={handleRowDoubleClick}
            paginationPageSizeSelector={[100, 500, 1000, 5000]}
            onCellClicked={handleCellClicked}
            onGridReady={onGridReady}
            enableCellTextSelection={true}
            rowHeight={25}
            headerHeight={30}
            floatingFiltersHeight={28}
            overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
            tooltipShowDelay={0}
            getRowStyle={(params) => ({
              backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
            })}
          />
        )}
      </Box>
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        left: 70,
        fontSize: '14px',
        color: 'black',
      }}>
        Total Records: <b>{rowData.length}</b>
      </Box>
    </Box>
  );
};

export default GenerateCouponTable;